import React from 'react';
import { provider as providerApi } from '../services/api';

// ── Dynamic Field Renderer ──────────────────────────────────────
function DynamicField({ field, value, onChange, C }) {
  const base = {
    width:'100%',padding:'10px 12px',borderRadius:10,fontSize:13,
    border:`1px solid ${C.border}`,background:C.input,color:C.text,
    outline:'none',boxSizing:'border-box',
  };
  if (field.type === 'select') return (
    <select value={value||''} onChange={e=>onChange(field.key,e.target.value)} style={base}>
      <option value=''>Select {field.label}...</option>
      {(field.options||[]).map(o=><option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (field.type === 'multiselect') {
    const vals = Array.isArray(value) ? value : (value ? value.split(',') : []);
    return (
      <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
        {(field.options||[]).map(o=>{
          const sel = vals.includes(o);
          return <button key={o} type="button" onClick={()=>{
            const n = sel ? vals.filter(v=>v!==o) : [...vals,o];
            onChange(field.key, n.join(','));
          }} style={{padding:'4px 12px',borderRadius:20,fontSize:11,fontWeight:600,cursor:'pointer',border:'none',
            background:sel?'#1a3cc8':'rgba(0,0,0,0.06)',color:sel?'#fff':'#6b7280'}}>{o}</button>;
        })}
      </div>
    );
  }
  if (field.type === 'boolean') return (
    <div style={{display:'flex',gap:8}}>
      {['Yes','No'].map(opt=>{
        const sel = (opt==='Yes') === (value===true||value==='true'||value==='Yes');
        return <button key={opt} type="button" onClick={()=>onChange(field.key,opt==='Yes'?'true':'false')}
          style={{flex:1,padding:'8px',borderRadius:10,border:'none',cursor:'pointer',fontSize:12,fontWeight:600,
            background:sel?'#1a3cc8':'rgba(0,0,0,0.06)',color:sel?'#fff':'#6b7280'}}>{opt}</button>;
      })}
    </div>
  );
  if (field.type === 'textarea') return (
    <textarea value={value||''} onChange={e=>onChange(field.key,e.target.value)}
      placeholder={field.label} rows={3}
      style={{...base,resize:'vertical',fontFamily:'inherit'}}/>
  );
  return (
    <input type={field.type==='number'?'number':'text'} value={value||''}
      onChange={e=>onChange(field.key,e.target.value)}
      placeholder={field.label}
      style={base}/>
  );
}

// ── Dynamic Service Form ─────────────────────────────────────────
function DynamicServiceForm({ category, editData, onSave, onCancel, C }) {
  const pf = category?.provider_fields || {};
  const serviceFields = pf.service || [];
  const units = pf.units || ['per service','per hour','per day','per month'];

  const [form, setForm] = React.useState({
    name: editData?.name || '',
    description: editData?.description || '',
    price_txc: editData?.price_txc || '',
    unit: editData?.unit || units[0] || 'per service',
    sub_category_id: editData?.sub_category_id || '',
    ...((editData?.tags && typeof editData.tags === 'object') ? editData.tags : {}),
  });
  const [saving, setSaving] = React.useState(false);

  const setField = (key, val) => setForm(p=>({...p,[key]:val}));

  const handleSave = async () => {
    if (!form.name || !form.price_txc) return;
    setSaving(true);
    // Extract dynamic fields into tags
    const tags = {};
    serviceFields.forEach(f => { if (form[f.key] !== undefined) tags[f.key] = form[f.key]; });
    await onSave({ ...form, tags });
    setSaving(false);
  };

  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:16,marginBottom:12}}>
      <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:12}}>
        {editData ? 'Edit Service' : '+ Add New Service'}
      </div>

      {/* Service Name */}
      <div style={{marginBottom:10}}>
        <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:4}}>SERVICE NAME *</div>
        <input value={form.name} onChange={e=>setField('name',e.target.value)}
          placeholder="e.g. Full Home Cleaning" style={{width:'100%',padding:'10px 12px',
          borderRadius:10,fontSize:13,border:`1px solid ${C.border}`,background:C.input,
          color:C.text,outline:'none',boxSizing:'border-box'}}/>
      </div>

      {/* Dynamic service fields */}
      {serviceFields.map(field=>(
        <div key={field.key} style={{marginBottom:10}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:4}}>
            {field.label.toUpperCase()}{field.required?' *':''}
          </div>
          <DynamicField field={field} value={form[field.key]} onChange={setField} C={C}/>
        </div>
      ))}

      {/* Description */}
      <div style={{marginBottom:10}}>
        <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:4}}>DESCRIPTION</div>
        <textarea value={form.description} onChange={e=>setField('description',e.target.value)}
          placeholder="Describe your service..." rows={2}
          style={{width:'100%',padding:'10px 12px',borderRadius:10,fontSize:12,
          border:`1px solid ${C.border}`,background:C.input,color:C.text,
          outline:'none',resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}}/>
      </div>

      {/* Price + Unit */}
      <div style={{display:'flex',gap:8,marginBottom:12}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:4}}>PRICE (TXC) *</div>
          <input type="number" value={form.price_txc} onChange={e=>setField('price_txc',e.target.value)}
            placeholder="0" style={{width:'100%',padding:'10px 12px',borderRadius:10,fontSize:13,
            border:`1px solid ${C.border}`,background:C.input,color:C.text,outline:'none',boxSizing:'border-box'}}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:4}}>UNIT</div>
          <select value={form.unit} onChange={e=>setField('unit',e.target.value)}
            style={{width:'100%',padding:'10px 12px',borderRadius:10,fontSize:12,
            border:`1px solid ${C.border}`,background:C.input,color:C.text,outline:'none'}}>
            {units.map(u=><option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>

      <div style={{display:'flex',gap:8}}>
        <button onClick={onCancel} style={{flex:1,padding:'10px',borderRadius:10,border:`1px solid ${C.border}`,
          background:'transparent',color:C.muted,fontSize:13,cursor:'pointer',fontWeight:600}}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving||!form.name||!form.price_txc}
          style={{flex:2,padding:'10px',borderRadius:10,border:'none',cursor:'pointer',fontWeight:700,fontSize:13,
            background:(!form.name||!form.price_txc)?C.input:'#1a3cc8',
            color:(!form.name||!form.price_txc)?C.muted:'#fff'}}>
          {saving?'Saving...':editData?'Update Service':'Add Service'}
        </button>
      </div>
    </div>
  );
}


// ── Expiring Subscriptions Banner ───────────────────────────────
function ExpiringBanner({ C, nav }) {
  const [expiring, setExpiring] = React.useState([]);
  const [renewing, setRenewing] = React.useState(null);
  const [toast, setToast] = React.useState('');
  const showToast = m => { setToast(m); setTimeout(()=>setToast(''),3000); };

  React.useEffect(()=>{
    import('../services/api').then(({ provider })=>{
      provider.expiring().then(r=>setExpiring(r.expiring||[])).catch(()=>{});
    });
  },[]);

  if (!expiring.length) return null;

  const handleRenew = async (sub) => {
    setRenewing(sub.category_id);
    try {
      const { provider } = await import('../services/api');
      await provider.renew({ category_id: sub.category_id });
      setExpiring(p => p.filter(s => s.category_id !== sub.category_id));
      showToast(`✓ ${sub.category_name} renewed for 30 days!`);
    } catch(e) {
      showToast('❌ ' + (e.message || 'Renewal failed'));
    }
    setRenewing(null);
  };

  return (
    <div style={{marginBottom:16}}>
      {toast && <div style={{position:'fixed',top:80,left:'50%',transform:'translateX(-50%)',
        background:'#1a1a2e',color:'#fff',padding:'10px 20px',borderRadius:20,
        zIndex:99999,fontSize:13,fontWeight:600}}>{toast}</div>}
      <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:14,
        padding:'12px 14px',marginBottom:8}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
          <span style={{fontSize:16}}>⚠️</span>
          <div style={{fontSize:12,fontWeight:700,color:'#c2410c'}}>
            {expiring.length} subscription{expiring.length>1?'s':''} expiring soon
          </div>
        </div>
        {expiring.map(sub=>(
          <div key={sub.category_id} style={{display:'flex',alignItems:'center',
            justifyContent:'space-between',padding:'8px 0',
            borderTop:'1px solid #fed7aa'}}>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:'#1a1a1a'}}>{sub.category_name}</div>
              <div style={{fontSize:10,color:'#c2410c'}}>
                {sub.days_left <= 0 ? 'EXPIRED' : `${sub.days_left} day${sub.days_left===1?'':'s'} left`}
              </div>
            </div>
            <button onClick={()=>handleRenew(sub)}
              disabled={renewing===sub.category_id}
              style={{padding:'6px 14px',borderRadius:20,border:'none',cursor:'pointer',
                background:'#f97316',color:'#fff',fontSize:11,fontWeight:700,
                opacity:renewing===sub.category_id?0.6:1}}>
              {renewing===sub.category_id?'Renewing...':'Renew · '+sub.price_paid+' TXC'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// PROVIDER ONBOARDING WIZARD
// ══════════════════════════════════════════════════════════════
function ProviderOnboardingWizard({ category, C, nav, onComplete, onSkip }) {
  const [step, setStep] = React.useState(1); // 1-4 (skip doc upload for now)
  const [closing, setClosing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState('');
  const showToast = m => { setToast(m); setTimeout(()=>setToast(''),3000); };

  // Step 2: profile fields
  const pf = category?.provider_fields || {};
  const profileFields = pf.profile || [];
  const [profileData, setProfileData] = React.useState({});
  const [displayName, setDisplayName] = React.useState('');
  const [bio, setBio] = React.useState('');
  const [location, setLocation] = React.useState('');

  // Step 3: first service
  const [showSvcForm, setShowSvcForm] = React.useState(false);
  const [services, setServices] = React.useState([]);
  const [svcSaving, setSvcSaving] = React.useState(false);

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const close = (completed=false) => {
    setClosing(true);
    setTimeout(()=>{ completed ? onComplete() : onSkip(); }, 300);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { provider } = await import('../services/api');
      await provider.saveProfile({
        category_id: category.id,
        profile_data: profileData,
        display_name: displayName,
        bio,
        location,
      });
      setStep(3);
    } catch(e) { showToast('❌ ' + (e.message||'Failed to save')); }
    setSaving(false);
  };

  const addService = async (formData) => {
    setSvcSaving(true);
    try {
      const { provider } = await import('../services/api');
      await provider.addService({
        category_id: category.id,
        name: formData.name,
        description: formData.description||'',
        price_txc: parseFloat(formData.price_txc)||0,
        unit: formData.unit||'per service',
        tags: formData.tags||{},
      });
      setServices(p=>[...p, formData]);
      setShowSvcForm(false);
      showToast('✓ Service added!');
    } catch(e) { showToast('❌ ' + (e.message||'Failed')); }
    setSvcSaving(false);
  };

  const stepLabels = ['Category','Profile','Services','Go Live'];

  return (
    <div style={{position:'fixed',inset:0,zIndex:20000,display:'flex',flexDirection:'column',
      background: closing ? 'transparent' : 'rgba(0,0,0,0.7)',
      transition:'background 0.3s'}}>
      {toast && <div style={{position:'fixed',top:80,left:'50%',transform:'translateX(-50%)',
        background:'#1a1a2e',color:'#fff',padding:'10px 20px',borderRadius:20,
        zIndex:99999,fontSize:13,fontWeight:600}}>{toast}</div>}

      <div style={{
        flex:1,display:'flex',flexDirection:'column',background:'#fff',
        animation: closing ? 'sheetDown 0.3s forwards' : 'sheetUp 0.32s cubic-bezier(0.32,0.72,0,1)',
      }}>
        {/* Blue header */}
        <div style={{background:'#1a3cc8',padding:'0 0 0 0',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px 10px'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              {step > 1 && (
                <button onClick={()=>setStep(s=>s-1)} style={{background:'rgba(255,255,255,0.15)',
                  border:'none',borderRadius:10,width:34,height:34,color:'#fff',fontSize:16,cursor:'pointer',
                  display:'flex',alignItems:'center',justifyContent:'center'}}>←</button>
              )}
              <div>
                <div style={{fontSize:16,fontWeight:800,color:'#fff'}}>
                  {["Category Selected","Your Profile","Add Services","You are Live!"][step-1]}
                </div>
                <div style={{fontSize:11,color:'rgba(255,255,255,0.65)'}}>
                  Step {step} of {totalSteps}
                </div>
              </div>
            </div>
            <button onClick={()=>close(false)} style={{background:'rgba(255,255,255,0.15)',
              border:'none',borderRadius:10,width:34,height:34,color:'#fff',fontSize:18,cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
          </div>

          {/* Step dots */}
          <div style={{display:'flex',alignItems:'center',padding:'0 16px 12px',gap:0}}>
            {stepLabels.map((label,i)=>(
              <React.Fragment key={i}>
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                  <div style={{width:26,height:26,borderRadius:'50%',display:'flex',
                    alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0,
                    background: i+1 < step ? 'rgba(255,255,255,0.9)' : i+1===step ? '#fff' : 'rgba(255,255,255,0.2)',
                    color: i+1 <= step ? '#1a3cc8' : 'rgba(255,255,255,0.6)',
                    boxShadow: i+1===step ? '0 0 0 3px rgba(255,255,255,0.3)' : 'none'}}>
                    {i+1 < step ? '✓' : i+1}
                  </div>
                  <div style={{fontSize:9,color: i+1<=step?'#fff':'rgba(255,255,255,0.4)',fontWeight:i+1===step?700:400}}>
                    {label}
                  </div>
                </div>
                {i < stepLabels.length-1 && (
                  <div style={{flex:1,height:2,background: i+1<step?'rgba(255,255,255,0.7)':'rgba(255,255,255,0.2)',
                    marginBottom:16,marginLeft:2,marginRight:2}}/>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Progress bar */}
          <div style={{height:3,background:'rgba(255,255,255,0.2)'}}>
            <div style={{height:'100%',background:'#fff',width:progress+'%',transition:'width 0.3s'}}/>
          </div>
        </div>

        {/* Content */}
        <div style={{flex:1,overflowY:'auto',padding:'16px'}}>

          {/* STEP 1: Confirm category */}
          {step===1 && (
            <div>
              <div style={{textAlign:'center',marginBottom:20}}>
                <div style={{fontSize:48,marginBottom:8}}>{category.icon||'📋'}</div>
                <div style={{fontSize:18,fontWeight:800,color:'#111',marginBottom:4}}>{category.name}</div>
                <div style={{fontSize:13,color:'#6b7280',marginBottom:12}}>
                  {category.description || 'Offer your services on VGO'}
                </div>
                <div style={{display:'inline-flex',alignItems:'center',gap:6,
                  background:'#eff3ff',border:'1px solid #c7d2fe',borderRadius:20,padding:'6px 16px'}}>
                  <span style={{fontSize:12,fontWeight:700,color:'#1a3cc8'}}>
                    ✓ Subscribed · {category.subscription_price||30} TXC/mo
                  </span>
                </div>
              </div>

              <div style={{background:'#f9fafb',borderRadius:14,padding:14,marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:700,color:'#111',marginBottom:8}}>What happens next:</div>
                {[
                  ['2','Fill your professional profile'],
                  ['3','Add your services & prices'],
                  ['4','Go live — start getting orders!'],
                ].map(([n,t])=>(
                  <div key={n} style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                    <div style={{width:24,height:24,borderRadius:'50%',background:'#1a3cc8',
                      color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize:11,fontWeight:700,flexShrink:0}}>{n}</div>
                    <div style={{fontSize:13,color:'#374151'}}>{t}</div>
                  </div>
                ))}
              </div>

              <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:12,padding:12,marginBottom:16}}>
                <div style={{fontSize:12,color:'#92400e',lineHeight:1.5}}>
                  💡 Complete your profile to appear in search results and receive requests from customers.
                </div>
              </div>

              <button onClick={()=>setStep(2)} style={{width:'100%',padding:13,borderRadius:14,
                border:'none',background:'#1a3cc8',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Set Up Profile →
              </button>
              <button onClick={()=>close(true)} style={{width:'100%',padding:11,borderRadius:14,
                border:'1px solid #e5e7eb',background:'transparent',color:'#6b7280',
                fontSize:13,cursor:'pointer',marginTop:8,fontWeight:600}}>
                Skip for Now
              </button>
            </div>
          )}

          {/* STEP 2: Dynamic profile form */}
          {step===2 && (
            <div>
              <div style={{fontSize:14,fontWeight:800,color:'#111',marginBottom:4}}>
                {category.icon} {category.name} — Profile
              </div>
              <div style={{fontSize:12,color:'#6b7280',marginBottom:16}}>
                These details will be shown to customers
              </div>

              {/* Display name */}
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:'#6b7280',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,marginBottom:5}}>
                  DISPLAY NAME *
                </div>
                <input value={displayName} onChange={e=>setDisplayName(e.target.value)}
                  placeholder="e.g. Ammu's Kitchen / Dr. Sharma"
                  style={{width:'100%',padding:'10px 12px',borderRadius:10,fontSize:13,
                  border:'1px solid #e5e7eb',background:'#f9fafb',color:'#111',
                  outline:'none',boxSizing:'border-box'}}/>
              </div>

              {/* Dynamic profile fields */}
              {profileFields.map(field=>(
                <div key={field.key} style={{marginBottom:12}}>
                  <div style={{fontSize:11,color:'#6b7280',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,marginBottom:5}}>
                    {field.label}{field.required?' *':''}
                  </div>
                  <DynamicField field={field}
                    value={profileData[field.key]}
                    onChange={(k,v)=>setProfileData(p=>({...p,[k]:v}))}
                    C={C}/>
                </div>
              ))}

              {/* Location */}
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:'#6b7280',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,marginBottom:5}}>
                  SERVICE LOCATION
                </div>
                <input value={location} onChange={e=>setLocation(e.target.value)}
                  placeholder="e.g. Hyderabad, Banjara Hills"
                  style={{width:'100%',padding:'10px 12px',borderRadius:10,fontSize:13,
                  border:'1px solid #e5e7eb',background:'#f9fafb',color:'#111',
                  outline:'none',boxSizing:'border-box'}}/>
              </div>

              {/* Bio */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,color:'#6b7280',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,marginBottom:5}}>
                  BIO / ABOUT
                </div>
                <textarea value={bio} onChange={e=>setBio(e.target.value)}
                  placeholder="Tell customers about yourself and your experience..."
                  rows={3} style={{width:'100%',padding:'10px 12px',borderRadius:10,fontSize:12,
                  border:'1px solid #e5e7eb',background:'#f9fafb',color:'#111',
                  outline:'none',resize:'none',fontFamily:'inherit',boxSizing:'border-box'}}/>
              </div>

              <button onClick={saveProfile} disabled={saving||!displayName}
                style={{width:'100%',padding:13,borderRadius:14,border:'none',cursor:'pointer',
                  fontWeight:700,fontSize:14,
                  background:(!displayName)?'#e5e7eb':'#1a3cc8',
                  color:(!displayName)?'#9ca3af':'#fff'}}>
                {saving?'Saving...':'Save & Continue →'}
              </button>
              <button onClick={()=>setStep(3)} style={{width:'100%',padding:11,borderRadius:14,
                border:'1px solid #e5e7eb',background:'transparent',color:'#6b7280',
                fontSize:13,cursor:'pointer',marginTop:8,fontWeight:600}}>
                Skip Profile
              </button>
            </div>
          )}

          {/* STEP 3: Add services */}
          {step===3 && (
            <div>
              <div style={{fontSize:14,fontWeight:800,color:'#111',marginBottom:4}}>Add Your Services</div>
              <div style={{fontSize:12,color:'#6b7280',marginBottom:16}}>
                List what you offer with prices. Add at least one service to go live.
              </div>

              {/* Services added */}
              {services.map((s,i)=>(
                <div key={i} style={{border:'1.5px solid #10b98130',borderRadius:12,
                  padding:'10px 14px',marginBottom:8,background:'#f0fdf4'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#111'}}>{s.name}</div>
                    <div style={{fontSize:13,fontWeight:800,color:'#1a3cc8'}}>{s.price_txc} TXC</div>
                  </div>
                  <div style={{fontSize:10,color:'#6b7280',marginTop:2}}>{s.unit}</div>
                </div>
              ))}

              {/* Add service form or button */}
              {showSvcForm ? (
                <DynamicServiceForm
                  category={category}
                  editData={null}
                  onSave={addService}
                  onCancel={()=>setShowSvcForm(false)}
                  C={C}
                />
              ) : (
                <button onClick={()=>setShowSvcForm(true)}
                  style={{width:'100%',padding:12,borderRadius:12,
                  border:'2px dashed #1a3cc8',background:'#eff3ff',
                  color:'#1a3cc8',fontSize:13,fontWeight:700,cursor:'pointer',marginBottom:12}}>
                  + Add Service
                </button>
              )}

              <button onClick={()=>setStep(4)} disabled={services.length===0}
                style={{width:'100%',padding:13,borderRadius:14,border:'none',
                  fontWeight:700,fontSize:14,cursor:services.length>0?'pointer':'not-allowed',
                  background:services.length===0?'#e5e7eb':'#1a3cc8',
                  color:services.length===0?'#9ca3af':'#fff'}}>
                {services.length===0?'Add at least 1 service':'Go Live →'}
              </button>
              <button onClick={()=>setStep(4)} style={{width:'100%',padding:11,borderRadius:14,
                border:'1px solid #e5e7eb',background:'transparent',color:'#6b7280',
                fontSize:13,cursor:'pointer',marginTop:8,fontWeight:600}}>
                Skip — Add Later
              </button>
            </div>
          )}

          {/* STEP 4: Go Live */}
          {step===4 && (
            <div style={{textAlign:'center',paddingTop:10}}>
              <div style={{fontSize:56,marginBottom:12}}>🎉</div>
              <div style={{fontSize:20,fontWeight:800,color:'#111',marginBottom:8}}>
                You're Live!
              </div>
              <div style={{fontSize:13,color:'#6b7280',lineHeight:1.6,marginBottom:20}}>
                Your profile is now visible to customers.
                You'll receive a notification when someone sends you a request.
              </div>

              {/* Stats */}
              <div style={{display:'flex',gap:8,marginBottom:20}}>
                {[
                  {label:'SERVICES', val: services.length||0, color:'#1a3cc8'},
                  {label:'DAYS ACTIVE', val: 30, color:'#7c3aed'},
                  {label:'ORDERS', val: 0, color:'#10b981'},
                ].map((s,i)=>(
                  <div key={i} style={{flex:1,background:`${s.color}12`,border:`1px solid ${s.color}22`,
                    borderRadius:12,padding:'10px 6px',textAlign:'center'}}>
                    <div style={{fontSize:20,fontWeight:900,color:s.color}}>{s.val}</div>
                    <div style={{fontSize:9,color:'#6b7280',fontWeight:600}}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Profile preview */}
              <div style={{border:'1.5px solid #e5e7eb',borderRadius:14,padding:14,
                textAlign:'left',marginBottom:16}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                  <div style={{width:40,height:40,borderRadius:'50%',
                    background:'linear-gradient(135deg,#1a3cc8,#7c3aed)',
                    display:'flex',alignItems:'center',justifyContent:'center',
                    fontSize:16,fontWeight:900,color:'#fff'}}>
                    {(displayName||'P').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontSize:14,fontWeight:800,color:'#111'}}>{displayName||'My Profile'}</div>
                    <div style={{fontSize:11,color:'#6b7280'}}>{category.icon} {category.name}</div>
                  </div>
                  <div style={{marginLeft:'auto',background:'#dbeafe',color:'#1a3cc8',
                    padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:700}}>
                    Active ✓
                  </div>
                </div>
                {location && <div style={{fontSize:11,color:'#6b7280'}}>📍 {location}</div>}
              </div>

              <button onClick={()=>close(true)}
                style={{width:'100%',padding:13,borderRadius:14,border:'none',
                background:'linear-gradient(135deg,#1a3cc8,#7c3aed)',
                color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',marginBottom:8}}>
                Go to My Dashboard →
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes sheetDown { from { transform: translateY(0); } to { transform: translateY(100%); } }
      `}</style>
    </div>
  );
}


export { ProviderOnboardingWizard, DynamicServiceForm, DynamicField };